import React, {useState, useEffect, useCallback, useRef} from 'react';
import Draggable from 'react-draggable';
import {
    Box, Dialog, DialogTitle, DialogContent, Typography, Paper, IconButton,
} from '@mui/material';
import ChatbotInterface from '@Pages/ChatBot/ChatBotInterface';
import useChatBotStyles from './ChatPopupStyles';
import {persistor} from '@Redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {resetState} from '@Redux/chatSlice';
import {ClearChatIcon} from '@Assets/ClearChatIcon';
import TooltippedIcon from '@Components/TooltippedIcon';
import axiosInstance from "@API/axios";
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon
    from '@mui/icons-material/CloseFullscreenRounded';
import RobotIcon from '@Assets/RobotIcon';
import ThemeCreator from '@Themes/themeCreator';
import {setSnackbar} from "@Redux/snackBarSlice";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FullscreenExitRoundedIcon
    from '@mui/icons-material/FullscreenExitRounded';
import {AI_CHAT_CLEAR_CONTEXT_URL} from "../../API/APIRoutes";
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';

// PaperComponent to enable dragging functionality for the dialog.
// It uses the Draggable component to make the dialog draggable.
const PaperComponent = (props) => {
    return (
        <Draggable
            handle="#draggable-dialog-title" // Only the dialog title is draggable.
            cancel={'[class*="MuiDialogContent-root"], .resize-handle'} // Prevent dragging by clicking on the content or resize handles.
            bounds="parent" // Restricts dragging within the parent element.
            onStart={props.onStart} // Callback for when dragging starts.
            onStop={props.onStop} // Callback for when dragging stops.
            disabled={props.isFullScreen} // Disable dragging in fullscreen mode
            position={props.isFullScreen ? {x: 0, y: 0} : undefined} // Force position to 0,0 in fullscreen
        >
            <Paper {...props} />
        </Draggable>
    );
};

// Main ChatPopup component that manages the chat dialog popup.
const ChatPopup = () => {
    const theme = ThemeCreator();
    const [open, setOpen] = useState(false); // State to manage the open/close status of the dialog.
    const [dialogState, setDialogState] = useState({
        size: {width: 500, height: 550}, // Initial size of the dialog.
        position: {x: 16, y: 110}, // Initial position of the dialog.
        isFullScreen: false, // State to manage full-screen mode.
        isResized: false, // State to track if the dialog has been resized.
    });

    const styles = useChatBotStyles(); // Hook for custom styles.
    const [isDragging, setIsDragging] = useState(false); // State to track if the dialog is currently being dragged.
    const userId = useSelector((state) => state.user.id);
    const minHeight = 200; // Minimum height for resizing the dialog.
    const minWidth = 300; // Minimum width for resizing the dialog.
    const initialSize = {width: 500, height: 550}; // Initial size constants.
    const fullScreenSize = {width: '100vw', height: '100vh'}; // Fullscreen size constants.
    const resizeStateRef = useRef({
        isResizing: false,
        resizeDirection: null,
        startX: 0,
        startY: 0,
        startWidth: 0,
        startHeight: 0,
        startLeft: 0,
        startTop: 0
    });

    const dispatch = useDispatch(); // Hook to dispatch actions to Redux.


    // Toggles the dialog open/close state when the chat icon is clicked.
    const onClickChatIcon = () => {
        if (!open) {
            // Calculate position based on window size to simulate bottom-right positioning
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const dialogWidth = initialSize.width;
            const dialogHeight = initialSize.height;

            setDialogState({
                size: initialSize,
                position: {
                    x: windowWidth - dialogWidth - 24, // 24px from right edge
                    y: windowHeight - dialogHeight - 120 // 120px from bottom
                },
                isFullScreen: false,
                isResized: false,
            });
        }
        setOpen((prev) => !prev);
    };

    // Callbacks to set dragging state.
    const onStart = () => {
        setIsDragging(true); // Sets dragging to true when drag starts.
    };
    const onStop = () => {
        setIsDragging(false); // Sets dragging to false when drag stops.
    };

    // Callback to reset the dialog size to its initial size.
    const handleResetSize = useCallback(() => {
        setDialogState((prevState) => ({
            ...prevState,
            size: initialSize,
            isResized: false,
        }));
    }, []);

    // Function to toggle fullscreen mode
    const toggleFullScreen = useCallback(() => {
        setDialogState((prevState) => {
            if (!prevState.isFullScreen) {
                // Entering fullscreen - always center at 0,0
                return {
                    ...prevState,
                    isFullScreen: true,
                    size: fullScreenSize,
                    position: {x: 0, y: 0},
                    isResized: false,
                };
            } else {
                // Exiting fullscreen - return to bottom-right positioning
                return {
                    ...prevState,
                    isFullScreen: false,
                    size: initialSize,
                    position: {
                        x: window.innerWidth - initialSize.width - 24,
                        y: window.innerHeight - initialSize.height - 120
                    },
                    isResized: false,
                };
            }
        });
    }, [fullScreenSize, initialSize]);

    // Function to handle mouse movement and update dialog size.
    const handleMouseMove = (moveEvent) => {
        if (!resizeStateRef.current.isResizing) return;

        const { resizeDirection, startX, startY, startWidth, startHeight, initialX, initialY } = resizeStateRef.current;
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;
        let newX = initialX;
        let newY = initialY;

        // Handle different resize directions
        if (resizeDirection?.includes('right')) {
            newWidth = Math.max(minWidth, startWidth + deltaX);
            newX = initialX; // Keep position fixed for right edge
        }
        if (resizeDirection?.includes('left')) {
            newWidth = Math.max(minWidth, startWidth - deltaX);
            newX = initialX + (startWidth - newWidth); // Adjust position for left edge
        }
        if (resizeDirection?.includes('bottom')) {
            newHeight = Math.max(minHeight, startHeight + deltaY);
            newY = initialY; // Keep position fixed for bottom edge
        }
        if (resizeDirection?.includes('top')) {
            const newHeightCandidate = Math.max(minHeight, startHeight - deltaY);
            const heightDifference = startHeight - newHeightCandidate;
            newHeight = newHeightCandidate;
            newY = initialY + heightDifference; // Adjust position for top edge
        }

        setDialogState((prevState) => ({
            ...prevState,
            size: { width: newWidth, height: newHeight },
            position: { x: newX, y: newY },
            isResized: newWidth !== initialSize.width || newHeight !== initialSize.height,
        }));
    };

    // Function to handle the end of mouse movement.
    const handleMouseUp = () => {
        if (resizeStateRef.current.isResizing) {
            resizeStateRef.current.isResizing = false;
            resizeStateRef.current.resizeDirection = null;
            document.body.style.cursor = 'default';
        }
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        // This function is executed when the component unmounts
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);
    // Handles resizing the dialog using mouse events.
    const handleResizeStart = (event, direction) => {
        event.preventDefault();
        event.stopPropagation();

        resizeStateRef.current = {
            isResizing: true,
            resizeDirection: direction,
            startX: event.clientX,
            startY: event.clientY,
            startWidth: dialogState.size.width,
            startHeight: dialogState.size.height,
            initialX: dialogState.position.x,
            initialY: dialogState.position.y
        };

        // Set cursor based on direction
        const cursorMap = {
            'top': 'n-resize',
            'bottom': 's-resize',
            'left': 'w-resize',
            'right': 'e-resize',
            'top-left': 'nw-resize',
            'top-right': 'ne-resize',
            'bottom-left': 'sw-resize',
            'bottom-right': 'se-resize'
        };
        document.body.style.cursor = cursorMap[direction];
    };

   // Add these state variables after your existing useState declarations
    const [clearChatLoading, setClearChatLoading] = useState(false);
    const [clearContextLoading, setClearContextLoading] = useState(false);

// Update the handleClearChat function
const handleClearChat = async () => {
    // Stop any currently playing speech immediately
    window.speechSynthesis.cancel();
    setClearChatLoading(true); // Start loadingAdd commentMore actions
    try {
        const response = await axiosInstance.post(AI_CHAT_CLEAR_CONTEXT_URL);
        await persistor.purge();
        dispatch(resetState({userId}));
    } catch (error) {
        console.error('Error clearing chat :', error);
        dispatch(
            setSnackbar({
                open: true,
                message: "Failed to clear chat . Please try again.",
                error: true,
            })
        );
    } finally {
        setClearChatLoading(false); // End loading
    }

};

// Update the handleClearContext function
const handleClearContext = async () => {
    // Stop any currently playing speech immediately
    window.speechSynthesis.cancel();
    setClearContextLoading(true); // Start loadingAdd commentMore actions
    try {
        const response = await axiosInstance.post(AI_CHAT_CLEAR_CONTEXT_URL);

        dispatch(
            setSnackbar({
                open: true,
                message: "Context cleared successfully.",
                error: false,
            })
        );
    } catch (error) {
        console.error('Error clearing context:', error);
        dispatch(
            setSnackbar({
                open: true,
                message: "Failed to clear context. Please try again.",
                error: true,
            })
        );
    } finally {
        setClearContextLoading(false); // End loading
    }

};
    const FullScreenIcon = (isFullScreen, toggleFullScreen) => (!isFullScreen ? (
            <TooltippedIcon
                title="FullScreen Window"
                placement="top"
                icon={<OpenInFullRoundedIcon sx={styles.fullScreenButton} onClick={toggleFullScreen}/>}
            />

        ) : (
            <TooltippedIcon
                title="Exit FullScreen"
                placement="top"
                icon={<CloseFullscreenRoundedIcon sx={styles.fullScreenButton} onClick={toggleFullScreen}/>}
            />

        )
    );

    // Resize handles component
    const ResizeHandles = () => (
        <>
            {/* Edge handles */}
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('top')}
                onMouseDown={(e) => handleResizeStart(e, 'top')}
            />
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('right')}
                onMouseDown={(e) => handleResizeStart(e, 'right')}
            />
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('bottom')}
                onMouseDown={(e) => handleResizeStart(e, 'bottom')}
            />
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('left')}
                onMouseDown={(e) => handleResizeStart(e, 'left')}
            />

            {/* Corner handles */}
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('top-left')}
                onMouseDown={(e) => handleResizeStart(e, 'top-left')}
            />
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('top-right')}
                onMouseDown={(e) => handleResizeStart(e, 'top-right')}
            />
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('bottom-left')}
                onMouseDown={(e) => handleResizeStart(e, 'bottom-left')}
            />
            <Box
                className="resize-handle"
                sx={styles.resizeHandle('bottom-right')}
                onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
            />
        </>
    );

    return (
        <>
            <TooltippedIcon
                title={open ? 'Close Unilever AI Assistant' : 'Ask Unilever AI Assistant'}
                placement={open ? 'left' : 'top'}
                icon={
                    <IconButton onClick={onClickChatIcon} sx={styles.popupButton}>
                        <RobotIcon
                            scale={65}
                            color={open ? theme.palette.white.main : theme.palette.primary.main}
                            bgColor={open ? theme.palette.primary.main : theme.palette.white.main}
                            isHappy={open}
                            isAnimated={open}
                        />
                    </IconButton>
                }
            />
            <Dialog
                open={open} // Control the open/close state of the dialog.
                fullScreen={dialogState.isFullScreen} // Make dialog fullscreen if isFullScreen is true.
                hideBackdrop={true} // Hide the backdrop behind the dialog.
                disableEnforceFocus // Prevent dialog from capturing focus automatically.
                disableAutoFocus // Prevent auto-focus when dialog opens.
                disableRestoreFocus // Prevent focus restoration when dialog closes.
                disableScrollLock // Allow background scrolling
                PaperComponent={PaperComponent} // Use custom draggable PaperComponent
                PaperProps={{
                    onStart: onStart, // Pass onStart callback to PaperComponent.
                    onStop: onStop, // Pass onStop callback to PaperComponent.
                    sx: styles.paperProps(dialogState), // Apply custom styles to PaperComponent.
                    isFullScreen: dialogState.isFullScreen, // Pass fullscreen state to PaperComponent
                }}
                sx={{
                    pointerEvents: 'none',
                    '& .MuiDialog-paper': {
                        pointerEvents: 'auto',
                    }
                }}
            >
                <DialogTitle
                    id="draggable-dialog-title" // ID to make the title draggable.
                    sx={styles.popupHeader(isDragging)} // Custom styles for dialog title.
                >
                    <Box sx={styles.popupHeaderIcons}> {/* Box for the header icons. */}
                        <RobotIcon
                            scale={43}
                            color={theme.palette.white.main}
                            bgColor={theme.palette.primary.main}
                            isHappy={true}
                            isAnimated={false}
                        />
                        <Typography variant="h6" fontWeight={'bold'} marginLeft={'6px'}>
                            UniServe AI Assistant
                        </Typography>
                        <TooltippedIcon
                            title="Close Window"
                            placement="top"
                            icon={<CloseRoundedIcon sx={styles.closeButton} onClick={onClickChatIcon}/>}
                        />
                        {FullScreenIcon(dialogState.isFullScreen, toggleFullScreen)} {/* Fullscreen icon toggle. */}
                        {dialogState.isResized && (<TooltippedIcon
                            title="Reset To Default Size"
                            placement="top"
                            icon={<FullscreenExitRoundedIcon sx={styles.resetButton} onClick={handleResetSize}/>}
                        />)}
                        <TooltippedIcon
                            title="Clear Chat History - Deletes all messages and clears AI memory. Use this to start a completely new conversation with the AI assistant.Note: Chat auto-deletes after 8 hours of inactivity"
                            placement="top"
                            icon={<DeleteSweepRoundedIcon
                                sx={styles.clearChatButton(clearChatLoading)}
                                onClick={!clearChatLoading ? handleClearChat : undefined}
                            />}
                        />
                        <TooltippedIcon
                            title="Clear Memory Context - Only clears AI memory but keeps your messages. Use this when the AI assistant is hallucinating or giving incorrect responses."
                            placement="top"
                            icon={<PsychologyAltRoundedIcon
                                sx={styles.clearContextButton(clearContextLoading)}
                                onClick={!clearContextLoading ? handleClearContext : undefined}
                            />}
                        />
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={styles.popupContent}> {/* Content area of the dialog. */}
                    <ChatbotInterface
                        popupWidth={dialogState.size.width}
                        isFullScreen={dialogState.isFullScreen}
                    />
                    {!dialogState.isFullScreen && <ResizeHandles />}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ChatPopup;
